import { useCookies } from '@vueuse/integrations/useCookies';
import { defineStore } from 'pinia';
import { ref, watch, watchEffect } from 'vue';

import { COUNTRIES } from '@/common/constants';
import { useAuthStore } from '@/common/stores/auth';
import { axios } from '@/helpers/axios';
import { storage } from '@/helpers/local-storage';
import { useSearchParams } from '@/modules/keywords/composables/router';
import type { CountryCode } from '@/types/country';
import type { Marketplace } from '@/types/marketplace';

interface Preference {
  searchMarketplace?: string;
  searchKeyword?: string;
  searchCountry?: string;
}

export const useTopbarStore = defineStore('topbar', () => {
  const { searchParams } = useSearchParams();

  const showConfirmRefreshDialog = ref(false);
  const refreshingDialog = ref(false);
  const cookies = useCookies(['searchMarketplace', 'searchKeyword', 'searchCountry']);
  const marketplaces = ref<Marketplace[]>([
    { name: 'All', code: 'all', countries: ['USA', 'GBR', 'AUS', 'CAN', 'FRA', 'DEU', 'EEA', 'IND', 'PHL', 'GLO'] },
    { name: 'Amazon', code: 'amazon', countries: ['USA', 'GBR', 'AUS', 'CAN', 'FRA', 'DEU', 'EEA'] },
    { name: 'Amazon Audible', code: 'amazon_audible', countries: ['GLO'] },
    { name: 'Amazon Books', code: 'amazon_books', countries: ['GLO', 'USA'] },
    { name: 'Amazon Kindle', code: 'amazon_kindle', countries: ['GLO', 'USA'] },
    { name: 'Barnes & Noble', code: 'barnesandnoble', countries: ['GLO'] },
    { name: 'eBay', code: 'ebay', countries: ['USA', 'GBR', 'AUS', 'CAN', 'FRA', 'DEU', 'EEA'] },
    { name: 'Etsy', code: 'etsy', countries: ['USA', 'GBR', 'AUS', 'CAN', 'FRA', 'DEU', 'EEA', 'IND', 'GLO'] },
    { name: 'Facebook Marketplace', code: 'facebook_marketplace', countries: ['USA'] },
    { name: 'Google Shopping', code: 'google_shopping', countries: ['USA'] },
    { name: 'Kobo', code: 'kobo', countries: ['USA'] },
    { name: 'Pinterest', code: 'pinterest', countries: ['USA', 'GBR', 'FRA', 'EEA', 'PHL'] },
    { name: 'Shein', code: 'shein', countries: ['GLO'] },
    { name: 'Shopee', code: 'shopee', countries: ['GLO'] },
    { name: 'Shutterstock', code: 'shutterstock', countries: ['GLO', 'USA', 'GBR', 'CAN', 'AUS', 'FRA', 'DEU', 'IND'] },
    { name: 'Target', code: 'target', countries: ['USA'] },
    { name: 'Teachers Pay Teachers', code: 'teacherspayteachers', countries: ['USA'] },
    { name: 'Temu', code: 'temu', countries: ['GLO'] },
    { name: 'TikTok', code: 'tiktok', countries: ['GLO'] },
    { name: 'Walmart', code: 'walmart', countries: ['USA', 'CAN'] },
  ]);
  const availableCountries = ref<CountryCode[]>([...COUNTRIES]);

  const getCountryFromUrl = (): string | null => {
    try {
      return new URLSearchParams(window.location.search).get('country');
    } catch {
      return null;
    }
  };

  const findMarketplace = (code: string) => {
    const marketplace = marketplaces.value.find(marketplace => marketplace.code.toLowerCase() === code.toLowerCase());

    return marketplace ?? findMarketplace('etsy');
  };
  const findCountry = (code: string) => availableCountries.value.find(country => country.toLowerCase() === code.toLowerCase());
  const selectedMarketplace = ref<Marketplace | null>(cookies.get('searchMarketplace') ? findMarketplace(cookies.get('searchMarketplace')) : findMarketplace('etsy'));
  const updateCountryOptions = () => selectedMarketplace.value?.countries.map(countryCode => availableCountries.value.find(country => country.toLowerCase() === countryCode.toLowerCase())).filter(Boolean) as CountryCode[];
  const selectedCountry = ref<CountryCode | null>(storage.get<string>('searchCountry') ? findCountry(storage.get<string>('searchCountry')) : findCountry('USA'));
  const keyword = ref(cookies.get('searchKeyword') || '');
  const preferences = ref<Preference>({});
  const countries = ref<CountryCode[]>(updateCountryOptions());
  const isSyncing = ref(false);
  const lastSync = ref(null);
  const isLoadingLastSync = ref(true);

  const refreshListings = (payload) => axios.post('/refresh-data/listings', payload);

  const checkRefreshJobStatus = (uuid: string) => axios.get(`/refresh-data/listings/${uuid}/status`);
  const deleteRefreshJob = (uuid: string) => axios.post(`/refresh-data/listings/${uuid}/delete`);
  const getLastRefreshTime = async () => {
    if (!useAuthStore().isUserLogged) {
      return;
    }

    isLoadingLastSync.value = true;
    const response = await axios.get('/refresh-data/listings/last-refresh');
    if (response.status === 200) {
      lastSync.value = response.data.num_hours;
    }
    isLoadingLastSync.value = false;
  };
  const processHealthCheck = (payload) => axios.post('/health-check/process', payload);
  

  watch(selectedMarketplace, (value) => {
    if (value) {
      countries.value = updateCountryOptions();
      if (selectedMarketplace.value.countries.includes(storage.get('searchCountry')) || selectedMarketplace.value.countries.includes(searchParams.value.country)) {
        const country = availableCountries.value.find((c) => c.toLowerCase() === storage.get<string>('searchCountry')?.toLowerCase());
        if (country) {
          selectedCountry.value = country;
        } else {
          const [firstCountry] = countries.value;
          selectedCountry.value = firstCountry || null;
        }
      } else {
        const [firstCountry] = countries.value;
        selectedCountry.value = firstCountry || null;
      }
    }
  });

  watchEffect(() => {
    keyword.value = searchParams.value.keyword;

    const countryFromUrl = getCountryFromUrl();
    const countryFromCookie = storage.get<string>('searchCountry');
    const preferredCountry = countryFromUrl || countryFromCookie || 'USA';
    const selected = findCountry(preferredCountry);

    if (selected) {
      selectedCountry.value = selected;
      if (countryFromUrl) {
        storage.set<string>('searchCountry', selected);
      }
    }

    selectedMarketplace.value = findMarketplace(searchParams.value.source ?? 'etsy');
  });

  return {
    showConfirmRefreshDialog,
    refreshingDialog,
    refreshListings,
    checkRefreshJobStatus,
    deleteRefreshJob,
    getLastRefreshTime,
    processHealthCheck,
    marketplaces,
    countries,
    availableCountries,
    selectedMarketplace,
    selectedCountry,
    keyword,
    preferences,
    findMarketplace,
    findCountry,
    updateCountryOptions,
    isSyncing,
    lastSync,
    isLoadingLastSync,
  };
});
